import { useState } from "react"

import { AssociateProductType } from "../../../types"

import { useEquivalentProductSearch } from "./useEquivalentProductSearch"

import type { CompetitorProduct, Product } from "@/graphql/codegen/graphql"
import type { AutocompleteOption } from "@/types/form-fields"

interface UseProductSearchReturn {
  // Search state
  productType: AssociateProductType
  searchTerm: string
  productId: number | undefined
  competitorProductId: string | undefined
  showInactive: boolean

  // Search results (from useEquivalentProductSearch)
  products: Product[] | CompetitorProduct[]
  loading: boolean

  // Handlers
  handleTypeChange: (type: AssociateProductType) => void
  handleSearchChange: (searchedItem: string | AutocompleteOption) => void
  handleToggleInactiveView: () => void
}

export const useProductSearch = (): UseProductSearchReturn => {
  const [productType, setProductType] = useState<AssociateProductType>(AssociateProductType.Akrochem)
  const [searchTerm, setSearchTerm] = useState("")
  const [productId, setProductId] = useState<number | undefined>(undefined)
  const [competitorProductId, setCompetitorProductId] = useState<string | undefined>(undefined)
  const [showInactive, setShowInactive] = useState(true)

  const { products, loading } = useEquivalentProductSearch({
    productType,
    searchTerm,
    productId,
    competitorProductId,
    showInactive,
  })

  const handleTypeChange = (type: AssociateProductType) => {
    setProductType(type)
    // Clear search state when switching types
    setSearchTerm("")
    setProductId(undefined)
    setCompetitorProductId(undefined)
  }

  const handleSearchChange = (searchedItem: string | AutocompleteOption) => {
    if (typeof searchedItem === "string") {
      // Text Search Mode: User is typing in search bar
      setSearchTerm(searchedItem)
      setProductId(undefined)
      setCompetitorProductId(undefined)
    } else {
      // Dropdown Selection Mode: User selected specific product from autocomplete
      // Set ONLY the ID for exact lookup, clear searchTerm to avoid sending both
      setSearchTerm("")

      if (productType === AssociateProductType.Akrochem) {
        setProductId(Number(searchedItem.id))
        setCompetitorProductId(undefined)
      } else {
        setCompetitorProductId(String(searchedItem.id))
        setProductId(undefined)
      }
    }
  }

  const handleToggleInactiveView = () => {
    setShowInactive((prev) => !prev)
  }

  return {
    productType,
    searchTerm,
    productId,
    competitorProductId,
    showInactive,

    products,
    loading,

    handleTypeChange,
    handleSearchChange,
    handleToggleInactiveView,
  }
}
